import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useHistory } from 'react-router-dom';

import { useHasRestoredThisSession } from './useHasRestoredThisSession';
import { useOfferFlowMemoryState } from './useOfferFlowMemoryState';
import { useWriteOfferStepOnMount } from './useSaveOfferStep';

// Redirects the customer to their persisted step (once per session), OR resets
// the step back to 'offer' if we're not restoring. These two concerns share one
// hook because splitting them would race: the reset write would fire before the
// redirect completes, nuking the step the customer is about to be restored to.
export const useRestoreOfferConfigurationStep = (): void => {
    const history = useHistory();

    const {
        hasSavedProgress,
        restoreRoute,
        restoreSearch,
        offerId,
        step,
        amount,
        sweep,
        isEnabled
    } = useOfferFlowMemoryState();

    const { hasRestored, markRestored } = useHasRestoredThisSession(offerId);

    const canRestore = hasSavedProgress && isEnabled && !hasRestored;

    // Restore: redirect the customer to their persisted step (once per session)
    useEffect(() => {
        if (!canRestore || !restoreRoute || !restoreSearch || !offerId || !step)
            return;

        tracking.ampli.stepRestored({ offer_id: offerId, step });
        markRestored();
        history.push(
            maintainStateKey({ pathname: restoreRoute, search: restoreSearch })
        );
    }, [
        canRestore,
        restoreRoute,
        restoreSearch,
        offerId,
        step,
        history,
        markRestored
    ]);

    // Reset step to 'offer' so a future visit doesn't restore to a stale position
    const shouldResetStep = hasSavedProgress && !canRestore;

    useWriteOfferStepOnMount({
        offerId,
        step: 'offer',
        amount: shouldResetStep ? amount : undefined,
        sweep: shouldResetStep ? sweep : undefined
    });
};
